import {inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {AuthSessionApiService, BiometryVendor, ZoomCheckLivenessRequest} from '@npck/auth-frontend-api';
import {isMobile, LocalizationService, REQUEST_ID_HEADER, SentryService} from '@npck/libs/commons';
import {Observable, Subject, Subscription} from 'rxjs';
import {LivenessServiceResponse, LivenessServiceStatus} from '../../../pages/liveness-device/liveness-device.component';
import {AuthHolderService} from '../../auth-holder.service';
import {LivenessService} from '../../liveness.service';
import {Liveness} from '../liveness';
import {ScriptLoaderService} from '../utils/script-loader.service';
import {FACE_TEC_SDK_SCRIPT, FaceTecZoomState} from './facetec-customization';
import {NpckFaceTecApp} from './utils/NpckFaceTecAppController';

const SDK_LOAD_TIMEOUT_MS = 15000;

const LOGGED_ERROR_STATES = new Set<FaceTecZoomState>([
    FaceTecZoomState.CAMERA_NOT_FOUND,
    FaceTecZoomState.COULD_NOT_BE_LOADED,
    FaceTecZoomState.CANCELLED_BY_USER,
    FaceTecZoomState.CANCELLED_BY_FACE_TEC,
    FaceTecZoomState.ORIENTATION_ERROR,
    FaceTecZoomState.NETWORK_ERROR,
    FaceTecZoomState.DEVICE_NOT_SUPPORTED,
    FaceTecZoomState.UNKNOWN,
]);

@Injectable()
export class FaceTecLivenessVendorService implements Liveness, OnDestroy {

    private scriptLoader = inject(ScriptLoaderService) as ScriptLoaderService<typeof FaceTecSDK>;

    private mobile: boolean;
    private stateSubscription: Subscription;
    private destroyed = false;

    constructor(
        private authSessionApiService: AuthSessionApiService,
        private authHolderService: AuthHolderService,
        private localizationService: LocalizationService,
        private sentryService: SentryService,
        private livenessService: LivenessService,
        private ngZone: NgZone,
    ) {
        this.mobile = isMobile();
    }

    init(): void {
        this.scriptLoader.load({
            src: FACE_TEC_SDK_SCRIPT,
            globalName: 'FaceTecSDK',
            timeoutMs: SDK_LOAD_TIMEOUT_MS,
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.stateSubscription?.unsubscribe();
    }

    startLiveness(): Observable<LivenessServiceResponse> {
        const result$ = new Subject<LivenessServiceResponse>();

        const checkSdkReady = (): void => {
            if (this.destroyed) {
                return;
            }
            if (this.scriptLoader.value()) {
                NpckFaceTecApp.initSdk();
                this.subscribeToLiveness(result$);
            } else if (this.scriptLoader.error()) {
                this.onSdkLoadError(this.scriptLoader.error(), result$);
            } else {
                setTimeout(() => checkSdkReady(), 100);
            }
        };
        checkSdkReady();

        return result$.asObservable();
    }

    skipLiveness(): Observable<LivenessServiceResponse> {
        const result$ = new Subject<LivenessServiceResponse>();
        const request: ZoomCheckLivenessRequest = {
            vendor: BiometryVendor.ZOOM,
            faceScan: 'ZHVtbXk=',
            auditTrailHighQualityImageBase64List: ['ZHVtbXk='],
            auditTrailLowQualityImageBase64List: ['ZHVtbXk='],
        };
        this.authSessionApiService.checkLiveness(request, 'response')
            .subscribe(response => {
                result$.next({
                    status: LivenessServiceStatus.CLOSED,
                    requestId: response.headers.get(REQUEST_ID_HEADER),
                    rootStatus: response.body.status,
                });
            });
        return result$.asObservable();
    }

    private subscribeToLiveness(result$: Subject<LivenessServiceResponse>): void {
        this.stateSubscription?.unsubscribe();
        this.stateSubscription = NpckFaceTecApp.initLiveness(
            this.authSessionApiService,
            this.authHolderService,
            this.sentryService,
            this.livenessService,
            this.localizationService.currentLanguage?.locale,
            this.mobile,
            () => this.destroyed,
        ).subscribe(state => {
            this.runInZone(() => {
                const response = this.mapState(state.status, state.requestId, state.rootStatus, state.errorSource);
                if (response) {
                    result$.next(response);
                }
            });
        });
    }

    private onSdkLoadError(error: Error, result$: Subject<LivenessServiceResponse>): void {
        this.livenessService.logError(FaceTecZoomState.COULD_NOT_BE_LOADED, error?.message);
        if (error?.message?.includes('not found after')) {
            result$.next({status: LivenessServiceStatus.NETWORK_PROBLEM, rootStatus: 'FACETEC_SDK_TIMEOUT'});
        } else {
            result$.next({status: LivenessServiceStatus.NETWORK_PROBLEM, rootStatus: 'FACETEC_SDK_LOAD_FAILED'});
        }
    }

    private mapState(
        status: FaceTecZoomState,
        requestId: string | undefined,
        rootStatus: string,
        errorSource: string | undefined,
    ): LivenessServiceResponse | null {
        const statusMap: Partial<Record<FaceTecZoomState, LivenessServiceStatus>> = {
            [FaceTecZoomState.STARTED]: LivenessServiceStatus.LIVENESS_STARTED,
            [FaceTecZoomState.SUCCEED]: LivenessServiceStatus.SUCCEED,
            [FaceTecZoomState.CLOSED]: LivenessServiceStatus.CLOSED,
            [FaceTecZoomState.LIVENESS_FAILED]: LivenessServiceStatus.LIVENESS_FAILED,
            [FaceTecZoomState.FACE_NOT_MATCHED]: LivenessServiceStatus.FACE_NOT_MATCHED,
            [FaceTecZoomState.LIMIT_PER_SESSION_EXCEEDED]: LivenessServiceStatus.LIMIT_PER_SESSION_EXCEEDED,
            [FaceTecZoomState.CANCELLED_BY_USER]: LivenessServiceStatus.CANCEL,
            [FaceTecZoomState.CANCELLED_BY_FACE_TEC]: LivenessServiceStatus.CANCEL,
            [FaceTecZoomState.CAMERA_NOT_FOUND]: LivenessServiceStatus.CAMERA_FAILED,
            [FaceTecZoomState.COULD_NOT_BE_LOADED]: LivenessServiceStatus.CAMERA_FAILED,
            [FaceTecZoomState.ORIENTATION_ERROR]: LivenessServiceStatus.ORIENTATION_ERROR,
            [FaceTecZoomState.NETWORK_ERROR]: LivenessServiceStatus.NETWORK_ERROR,
            [FaceTecZoomState.DEVICE_NOT_SUPPORTED]: LivenessServiceStatus.DEVICE_NOT_SUPPORTED,
            [FaceTecZoomState.UNKNOWN]: LivenessServiceStatus.LIVENESS_ERROR,
        };

        const mapped = statusMap[status];
        if (!mapped) {
            return null;
        }

        if (LOGGED_ERROR_STATES.has(status)) {
            this.livenessService.logError(status.toString(), errorSource);
        }

        return {status: mapped, requestId, rootStatus};
    }

    private runInZone(cb: () => void): void {
        if (NgZone.isInAngularZone()) {
            cb();
        } else {
            this.ngZone.run(() => cb());
        }
    }
}
